import * as _ from "lodash";
import { Observable, BehaviorSubject } from "rxjs";
import { filter, share } from 'rxjs/operators';


import { addProvider, IProviderDescriptor } from "../ServiceUtil";


import { IHttpHeaderDelegate } from "./http.delegate";
import { IHttpResponse } from "./types/http.types";
import { IAppConfig } from "./types/IApplicationConfig";

export interface IHttpHeader
{
    name: string;
    payload: any;
}

/**
 * @DESCRIPTION:
 *
 * http.provider.response.interceptor provides a response interceptor that will parse a API response and
 * determine what actions need to be taken based on the received response
 */

/**
 * ResponseInterceptor class will use the Http and API delegates to handle API responses and return a normalized
 * response to clients
 */
export class ResponseInterceptor implements IHttpHeaderDelegate
{
    /**
     * subject for delivering CDN Access Tokens through the cdnAccessTokens observable.
     * @type {any}
     */
    private httpHeaderSubject: BehaviorSubject<IHttpHeader> = null;
    /**
 * An observable (hot, subscribe returns most recent http header that was encountered by the
 * @type {any}
 */
    public httpHeaders: Observable<IHttpHeader> = null;

    private accessToken: string = "";

    public accessTokenSubject: BehaviorSubject<string> = new BehaviorSubject(this.accessToken);

    constructor(private SERVICE_CONFIG: IAppConfig)
    {
        this.onResponse = this.onResponse.bind(this);
        this.httpHeaderSubject = new BehaviorSubject(null);
        this.httpHeaders = this.httpHeaderSubject.pipe(share());

    }

    /**
     * Take an http response and check the http and API response codes.  Then get the actual response from the
     * api protocol and bass back to the caller
     * @param response is the HTTP response that was received from the API
     * @returns {any}
     */
    public onResponse(response: IHttpResponse)
    {
        // Handle legacy token-in-response-body (for backward compatibility)
        if (response && response.data && response.data.token && response.data.token.authToken)
        {
            this.accessToken = response.data.token.authToken;
            this.accessTokenSubject.next(this.accessToken);
        }

        // Handle JWT cookie-based session refresh detection
        this.detectSessionRefresh(response);
        
        return response;
    }

    /**
     * Detect when the server has refreshed JWT tokens.
     * The actual JWT cookies (X-Access-Token, X-Refresh) are handled automatically by the browser.
     * We just need to detect when they've been refreshed for logging/notification purposes.
     */
    private detectSessionRefresh(response: IHttpResponse): void
    {
        if (!response || !response.headers) {
            return;
        }

        // Check if server has automatically refreshed the JWT tokens
        const tokenRefreshed = response.headers['x-token-refreshed'] || 
                              response.headers['X-Token-Refreshed'];
        
        if (tokenRefreshed && String(tokenRefreshed).toLowerCase() === 'true') {
            console.log('🔄 JWT tokens automatically refreshed by server');
            
            // Notify any listeners that the session has been extended
            this.handleHttpHeader('x-token-refreshed', 'true');
        }

        // Check for session extension indicator
        const sessionExtended = response.headers['x-session-extended'] || 
                               response.headers['X-Session-Extended'];
        
        if (sessionExtended && String(sessionExtended).toLowerCase() === 'true') {
            console.log('⏰ Session automatically extended by server');
            this.handleHttpHeader('x-session-extended', 'true');
        }

        // Note: We don't need to read Set-Cookie headers because:
        // 1. X-Access-Token and X-Refresh are HTTP-only (invisible to JavaScript)
        // 2. Browser automatically handles cookie storage and forwarding
        // 3. Our job is just to ensure withCredentials=true for all requests
    }

    /**
     * Handle a specific http header based on its name and contents.
     * @param headerName is the name of the header to handle
     * @param header is the data from the header to handler
     */
    public handleHttpHeader(headerName: string, header: any)
    {
        this.httpHeaderSubject.next({ name: headerName, payload: header } as IHttpHeader);
    }
}